import { TreeChangeEventDetail } from '@platform-ui-kit/components-library'
import { WppTree } from '@platform-ui-kit/components-library-react'
import { MayBeNull } from '@wpp-open/core'
import React, { useEffect, useState } from 'react'

import { useFileApi } from 'api/files/queries/useFileApi'
import { useFilesTreeApi } from 'api/files/queries/useFilesTreeApi'
import { Flex } from 'components/common/flex/Flex'
import { HubItem } from 'pages/files/sidePanel/HubItem'
import { useHubsWithAccessToFiles, createTree } from 'pages/files/utils'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { AssignmentType, FileItemType, FileTreeItem } from 'types/files/files'
import { Hub } from 'types/hubs/hubs'

interface Props {
  filesTreeItems?: MayBeNull<FileTreeItem[]>
  folderId?: string
  hubId?: string
  setFolderId: (folderId: string) => void
}

const FilesTree = ({ filesTreeItems, folderId, hubId, setFolderId }: Props) => {
  const [treeData, setTreeData] = useState(createTree(filesTreeItems))
  const [treeKey, setTreeKey] = useState(1)

  useEffect(() => {
    setTreeData(createTree(filesTreeItems, folderId))
  }, [folderId, filesTreeItems])

  useEffect(() => {
    setTreeKey(id => id + 1)
  }, [filesTreeItems])

  const handleTreeChange = (event: CustomEvent<TreeChangeEventDetail>) => {
    const {
      detail: { currentItem },
    } = event

    const folderId = String(currentItem && currentItem.id)
    if (folderId && hubId) {
      setFolderId(folderId)
    }
    setTreeData(event.detail.treeState)
  }

  return (
    !!filesTreeItems?.length && (
      <WppTree data-testid="files-tree" key={treeKey} multiple={false} data={treeData} onWppChange={handleTreeChange} />
    )
  )
}

interface DestinationSelectProps {
  setSelectedFolderId: (folderId: string | undefined) => void
  setSelectedHub: (hubId: Hub) => void
  selectedFolderId?: string
  selectedHub?: Hub
}

export const DestinationSelect = ({
  setSelectedFolderId,
  setSelectedHub,
  selectedFolderId,
  selectedHub,
}: DestinationSelectProps) => {
  const { currentTenant } = useCurrentTenantData()
  const { isLoading: isLoadingHubsWithAccessToFiles, hubsWithAccessToFiles } = useHubsWithAccessToFiles({
    sortBy: 'name',
  })
  const { data: fileFolder } = useFileApi({
    params: { id: selectedFolderId! },
    enabled: !!selectedFolderId,
  })

  const { isLoading: isFileTreeLoading, data: filesTreeItems } = useFilesTreeApi({
    params: { assignmentId: selectedHub?.id || '', tenantId: currentTenant.id, assignmentType: AssignmentType.HUB },
    enabled: !!selectedHub?.id,
  })

  const onHubSelect = (hub: Hub) => {
    setSelectedHub(hub)
    setSelectedFolderId(undefined)
  }

  return (
    <>
      <Flex direction="column">
        {isLoadingHubsWithAccessToFiles
          ? null
          : hubsWithAccessToFiles.map(hub => {
              const { id, name } = hub
              const hubIsSelected = selectedHub?.id === id
              const hubIsChecked =
                hubIsSelected &&
                (!selectedFolderId ||
                  (fileFolder?.type === FileItemType.SYSTEM_FOLDER && fileFolder?.assignmentId === selectedHub?.id))
              return (
                <React.Fragment key={id}>
                  <HubItem
                    key={id}
                    selected={hubIsSelected}
                    checked={hubIsChecked}
                    onClick={() => onHubSelect(hub)}
                    data-testid={`hub-item-${id}`}
                  >
                    {name}
                  </HubItem>
                  {hubIsSelected &&
                    (isFileTreeLoading ? null : (
                      <FilesTree
                        filesTreeItems={filesTreeItems}
                        hubId={selectedHub?.id}
                        folderId={selectedFolderId}
                        setFolderId={setSelectedFolderId}
                      />
                    ))}
                </React.Fragment>
              )
            })}
      </Flex>
    </>
  )
}
